import { Img, OpenImgContextProvider } from 'openimg/react'
import React from 'react'
import {
	data,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { Footer } from '#app/components/footer.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { NewBadge } from '#app/components/ui/new-badge.tsx'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon-48x48.png'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { useOptionalTheme, useTheme } from './routes/resources/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { cn, combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { getTheme, type Theme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

export const links: Route.LinksFunction = () => {
	return [
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/png', sizes: '48x48', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{ title: data ? 'Steel City Backgammon' : 'Error | Steel City Backgammon' },
		{ name: 'description', content: `Pittsburgh's place to play backgammon` },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							firstName: true,
							lastName: true,
							username: true,
							image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme: _theme = 'dark',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className="dark h-full overflow-x-hidden">
			<head>
				<title>Steel City Backgammon</title>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
				<script
					defer
					src="https://cloud.umami.is/script.js"
					data-website-id="41b410a3-d9eb-4c5d-99df-3458d40e6d19"
					nonce={nonce}
				/>
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function subscribeToScroll(callback: () => void) {
	window.addEventListener('scroll', callback, { passive: true })
	return () => window.removeEventListener('scroll', callback)
}

// True once the page has scrolled at all. Used to reveal the header's bottom
// border only when content actually passes underneath it — on short pages that
// never scroll, the border stays hidden so it isn't a distracting stray rule.
function useScrolled() {
	return React.useSyncExternalStore(
		subscribeToScroll,
		() => window.scrollY > 0,
		() => false,
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const theme = useTheme()
	useToast(data.toast)

	const [menuOpen, setMenuOpen] = React.useState(false)
	const scrolled = useScrolled()

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<div className="flex min-h-screen flex-col">
				<header
					className={`bg-background sticky top-0 z-40 border-b transition-colors ${
						scrolled ? 'border-border' : 'border-transparent'
					}`}
				>
					<nav className="relative container flex items-center justify-between py-6">
						{/* Brand */}
						<div className="hidden shrink-0 items-center gap-4 md:flex">
							<Logo width={50} height={50} />
							<span className="hidden text-xl lg:inline">
								Steel City Backgammon
							</span>
						</div>
						<div className="visible md:hidden"></div>

						{/* Desktop nav */}
						<div className="hidden items-center gap-2 md:flex">
							<NavLinks />
						</div>

						{/* Mobile toggle */}
						<Button
							variant="ghost"
							className="inline-flex items-center justify-center rounded-md p-2 md:hidden"
							aria-label="Open menu"
							aria-expanded={menuOpen}
							onClick={() => setMenuOpen((v) => !v)}
						>
							<span className="text-4xl">☰</span>
						</Button>

						{/* Mobile menu */}
						{menuOpen ? (
							<div className="bg-background ring-border absolute top-full right-0 z-50 mt-4 w-1/2 rounded-xl shadow-lg ring-1 md:hidden">
								<div className="flex flex-col gap-4 p-6">
									<NavLinks onNavigate={() => setMenuOpen(false)} />
								</div>
							</div>
						) : null}
					</nav>
				</header>

				<div className="flex flex-1 flex-col">
					<Outlet />
					<Footer />
				</div>
			</div>

			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</OpenImgContextProvider>
	)
}

export function Logo({ width = 150, height = 150 }) {
	return (
		<Link to="/" className="group grid leading-snug">
			<Img
				src="/img/sc-logo-circular@3x.png"
				className="rounded-2xl bg-black"
				width={width}
				height={height}
				fit="cover"
				alt="Steel City logo"
			/>
		</Link>
	)
}

type NavItem = {
	label: string
	to: string
	// Whether the tab shows in the navbar. Flip to false to hide any tab later
	// without removing its route or code. Hiding affects the navbar only — the
	// page stays reachable by direct URL.
	visible: boolean
	// Overlays a "NEW" comic-burst badge on the tab. Remove once it's not new.
	badge?: 'new'
}

const NAV_ITEMS: NavItem[] = [
	{ label: 'Home', to: '/', visible: true },
	{ label: 'Events', to: '/events', visible: true },
	{ label: 'Help Wanted', to: '/help-wanted', visible: false, badge: 'new' },
	{ label: 'Tournaments', to: '/tournaments', visible: true },
	{ label: 'About Us', to: '/about', visible: true },
	{ label: 'FAQ', to: '/faq', visible: true },
	{ label: 'Contact', to: '/contact', visible: true },
]

function NavLinks({ onNavigate }: { onNavigate?: () => void }) {
	const { pathname } = useLocation()

	const linkClass = (to: string) => {
		const isActive = to === '/' ? pathname === '/' : pathname.startsWith(to)
		return cn('px-0 text-lg whitespace-nowrap', isActive && 'ring-2')
	}

	return (
		<>
			{NAV_ITEMS.filter((item) => item.visible).map((item) => (
				<Button key={item.to} variant="ghost" size="sm" asChild>
					<Link
						to={item.to}
						className={linkClass(item.to)}
						onClick={onNavigate}
					>
						<span className="relative">
							{item.label}
							{item.badge === 'new' ? (
								<NewBadge
									size={22}
									className="pointer-events-none absolute -top-3 -right-4"
								/>
							) : null}
						</span>
					</Link>
				</Button>
			))}

			{/*{user ? (*/}
			{/*	<UserDropdown />*/}
			{/*) : (*/}
			{/*	<Button asChild variant="default" size="sm">*/}
			{/*		<Link to="/login" className="text-lg" onClick={onNavigate}>*/}
			{/*			Log In / Sign Up*/}
			{/*		</Link>*/}
			{/*	</Button>*/}
			{/*)}*/}
		</>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
